import { useState } from 'react';
import { X, FileText, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { scenariosApi } from '../../api/scenarios';

export default function CreateScenarioDialog({ 
  open, 
  onClose, 
  onSuccess, 
  partNumber,
  programId,
  scenarios 
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    copyFrom: 'blank'
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description
      };

      // If copying from existing scenario
      if (formData.copyFrom && formData.copyFrom !== 'blank') {
        payload.copy_from_scenario_id = formData.copyFrom;
      }

      const response = await scenariosApi.createScenario(partNumber, payload, { programId });

      if (response.success) {
        onSuccess(response.data);
        // Reset form
        setFormData({ name: '', description: '', copyFrom: 'blank' });
      } else {
        setError(response.error || 'Failed to create scenario');
      }
    } catch (err) {
      setError(err.message || 'Failed to create scenario');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '', copyFrom: 'blank' });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Scenario</DialogTitle>
          <DialogDescription>
            Create a new scenario to test different sourcing strategies or tariff impacts.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Scenario Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Scenario Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Mexico Alternative, China + Nearshore"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the key differences or assumptions..."
              rows={3}
            />
          </div>

          {/* Copy From */}
          <div className="space-y-2">
            <Label htmlFor="copyFrom">Start From</Label>
            <Select
              value={formData.copyFrom}
              onValueChange={(value) => setFormData({ ...formData, copyFrom: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>Blank Scenario</span>
                  </div>
                </SelectItem>
                {scenarios.map((scenario) => (
                  <SelectItem key={scenario.id} value={scenario.id}>
                    <div className="flex items-center gap-2">
                      <Copy className="h-4 w-4" />
                      <span>Copy from: {scenario.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {formData.copyFrom === 'blank' 
                ? 'Start with an empty scenario' 
                : 'Copy all forecast and cost data from selected scenario'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !formData.name}>
              {loading ? 'Creating...' : 'Create Scenario'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
